import { AssessmentPageInterface, AssessmentQuestionGroupInterface, AssessmentQuestionInterface } from 'app/assessment/models';
import { Recommendation, RecommendationInterface } from 'app/recommendation/models/recommendation.model';
import { sortRecommendationItemsByTitle } from 'app/shared/utilities/object-sort-methods.utility';

import { RecommendationTemplateInterface } from '../models';
import { RecommendationCategoryInterface } from '../models/recommendation-category.model';

import { RecommendationActions, RecommendationActionType } from './actions';
import { RecommendationState, RecommendationStateInterface } from './recommendation-state.model';


const initialState: RecommendationState = new RecommendationState({
  ids: [],
  recommendations: {},
  selectedRecommendation: 0,
  recommendationLibrary: undefined,
  selectedLibraryItem: undefined,
  assessmentQuestionData: undefined,
});

export function recommendationReducer(state: RecommendationStateInterface = initialState,
                                      action: RecommendationActions): RecommendationStateInterface {
  switch (action.type) {

    // Handle retrieving a fresh load
    case RecommendationActionType.LoadEmployeeRecommendations:
    case RecommendationActionType.LoadEmployeeRecommendationsFailed:
      return {
        ...state,
        ids: [],
        recommendations: {},
      };

    // Update the state when we get a new list
    case RecommendationActionType.LoadEmployeeRecommendationsCompleted:
      const allRecommendations: RecommendationInterface[] = action.payload as RecommendationInterface[];
      const newRecommendations: RecommendationInterface[] =
        allRecommendations.filter((recommendation: RecommendationInterface) => !state.recommendations[recommendation.id]);

      const newRecommendationIds: number[] =
        newRecommendations.map((recommendation: RecommendationInterface) => recommendation.id);
      const newRecommendationEntities: { [id: number]: RecommendationInterface } = newRecommendations.reduce(
        (recommendations: { [id: number]: RecommendationInterface }, recommendation: RecommendationInterface) => {
          return { ...recommendations, ...{
            [recommendation.id]: new Recommendation(recommendation),
          }};
        },
        {});

      return {
        ...state,
        ids: newRecommendationIds,
        recommendations: { ...state.recommendations, ...newRecommendationEntities },
      };

    // Apply the updated entity to state
    case RecommendationActionType.UpdateEmployeeRecommendationCompleted:
      const recommendation: RecommendationInterface = action.payload as RecommendationInterface;
      const updatedRecommendations: { [id: number]: RecommendationInterface } = { ...state.recommendations };
      updatedRecommendations[recommendation.id] = new Recommendation(recommendation);

      return {
        ...state,
        recommendations: updatedRecommendations,
      };

    // Update the selected entity in state
    case RecommendationActionType.SelectEmployeeRecommendationCompleted:
      return {
        ...state,
        selectedRecommendation: action.payload as number,
      };

    case RecommendationActionType.DeleteEmployeeRecommendationCompleted:
      const deletedId: number = action.payload as number;
      // there's probably a shorthand way to do this but I don't know....
      const finalRecommendations: { [id: number]: RecommendationInterface } = { ...state.recommendations };
      delete finalRecommendations[deletedId];

      return {
        ...state,
        recommendations: finalRecommendations,
      };

    case RecommendationActionType.LoadRecommendationsLibraryCompleted:
      const recommendationTemplateLibrary: RecommendationCategoryInterface[] = action.payload as RecommendationCategoryInterface[];

      return {
        ...state,
        recommendationLibrary: recommendationTemplateLibrary,
      };

    /**
     * Recommendation Template Category Create/Update/Delete
     */
    case RecommendationActionType.CreateRecommendationCategoryCompleted:

      return {
        ...state,
        recommendationLibrary:action.payload,
      };

    case RecommendationActionType.UpdateRecommendationCategoryCompleted:
      const updatedCategoryData: RecommendationCategoryInterface = action.payload;
      const updateLibraryCopy: RecommendationCategoryInterface[] =
        state.recommendationLibrary.map((category: RecommendationCategoryInterface) => {
          if(category.id === updatedCategoryData.id) {
            return {
              ...category,
              title: updatedCategoryData.title,
              categoryMullang: updatedCategoryData.categoryMullang,
            };
          }
          return category;
        });

      return {
        ...state,
        recommendationLibrary: updateLibraryCopy,
      };

    case RecommendationActionType.DeleteRecommendationCategory:
      const deletedCategoryData: RecommendationCategoryInterface = action.payload;
      const deletedCategoryLibraryCopy: RecommendationCategoryInterface[] =
        state.recommendationLibrary.filter((category: RecommendationCategoryInterface) => {
          if(category.id !== deletedCategoryData.id) {
            return category;
          }
        });
      return {
        ...state,
        recommendationLibrary: deletedCategoryLibraryCopy,
      };

    /**
     * Create/Update/Delete Recommendation Templates and rules
     */
    case RecommendationActionType.DeleteRecommendationTemplate:
    case RecommendationActionType.CreateRecommendationTemplate:
    case RecommendationActionType.CreateRecommendationTemplateRule:
    case RecommendationActionType.UpdateRecommendationTemplate:
    case RecommendationActionType.UpdateRecommendationTemplateRule:
      return {
        ...state,
        selectedLibraryItem: undefined,
      };

    case RecommendationActionType.CreateRecommendationTemplateCompleted:
    case RecommendationActionType.UpdateRecommendationTemplateCompleted:
    case RecommendationActionType.CreateRecommendationTemplateRuleCompleted:
    case RecommendationActionType.UpdateRecommendationTemplateRuleCompleted:
    case RecommendationActionType.DeleteRecommendationTemplateRuleCompleted:
      const newTemplate: RecommendationTemplateInterface = action.payload;

      const updatedLibrary: RecommendationCategoryInterface[] =
        state.recommendationLibrary.map((category: RecommendationCategoryInterface) => {
          // If the new/updated template belongs to this category, iterate through the existing items
          if(newTemplate.category === category.id) {
            let found: boolean = false;
            let updatedCategoryItems: RecommendationTemplateInterface[] =
              category.recommendations.map((item: RecommendationTemplateInterface) => {
                // If the item already exists (update path) then copy mutable values to existing instance
                if(item.id === newTemplate.id) {
                  found = true;
                  return {
                    ...item,
                    text: newTemplate.text,
                    priority: newTemplate.priority,
                    imageRef: newTemplate.imageRef,
                    rules: newTemplate.rules,
                  };
                }
                return item;
              });
            // If we didn't find the item in the existing collection, add to the collection and sort
            if(!found) {
              updatedCategoryItems.push(newTemplate);
              updatedCategoryItems = sortRecommendationItemsByTitle(updatedCategoryItems);
            }
            return {
              ...category,
              recommendations: updatedCategoryItems,
            };
          }
          // If the new/updated item isn't part of this collection - filter the collection to ensure it is removed
          // in the event it *used to be* part of the collection and was updated out
          return {
            ...category,
            recommendations: category.recommendations ? category.recommendations.filter((item: RecommendationTemplateInterface) => {
              if(item.id !== newTemplate.id) {
                return item;
              }
            }) : [],
          };
        });
      return {
        ...state,
        recommendationLibrary: updatedLibrary,
        selectedLibraryItem: newTemplate,
      };

    case RecommendationActionType.DeleteRecommendationTemplateCompleted:
      const deletedTemplate: RecommendationTemplateInterface = action.payload;

      const libraryWithoutTemplate: RecommendationCategoryInterface[] =
        state.recommendationLibrary.map((category: RecommendationCategoryInterface) => {
          if(category.id === deletedTemplate.category) {
            const categoryListWithoutTemplate: RecommendationTemplateInterface[] =
              category.recommendations.filter((item: RecommendationTemplateInterface) => {
                if(item.id !== deletedTemplate.id) {
                  return item;
                }
              });
            return {
              ...category,
              recommendations: categoryListWithoutTemplate,
            };
          }
          return category;
        });
      return {
        ...state,
        recommendationLibrary: libraryWithoutTemplate,
        selectedLibraryItem: deletedTemplate,
      };

    case RecommendationActionType.LoadAssessmentQuestionsCompleted:
      const questionData: AssessmentPageInterface[] = action.payload;

      let allQuestions: AssessmentQuestionInterface[] = [];
      const listStructure: AssessmentQuestionGroupInterface[] = questionData.map((page: AssessmentPageInterface) => {
        const pageQuestions: AssessmentQuestionInterface[] =
          Array.prototype.concat(...page.questionGroups.map((group: AssessmentQuestionGroupInterface) => {
            return group.questions;
          }));
        // since we've already gone through the work of flattening this groups questions, add them to the big list also....
        allQuestions = allQuestions.concat(pageQuestions);

        return {
          id: page.id,
          label: page.title,
          questions: pageQuestions,
        };
      });

      return {
        ...state,
        assessmentQuestionData: {
          listStructure,
          allQuestions,
        },
      };

    default:
      return state;
  }
}
